"use client"

import * as React from "react"
import Link from "next/link"
import { ArrowRight, Heart, Lightbulb, Zap, Globe, Coffee, Laptop, GraduationCap, Briefcase } from "lucide-react"
import { Button, Badge } from "@sarkiai/ui"
import { useTranslations } from "@/components/I18nProvider"

export default function CareersPage() {
  const t = useTranslations("careers")
  const v = useTranslations("values")

  const values = [
    {
      icon: Lightbulb,
      title: v("title1"),
      description: v("content1"),
    },
    {
      icon: Zap,
      title: v("title2"),
      description: v("content2"),
    },
    {
      icon: Heart,
      title: v("title3"),
      description: v("content3"),
    },
  ]

  const benefits = [
    { icon: Laptop, title: t("why_join.benefit1.title"), description: t("why_join.benefit1.desc") },
    { icon: GraduationCap, title: t("why_join.benefit2.title"), description: t("why_join.benefit2.desc") },
    { icon: Coffee, title: t("why_join.benefit3.title"), description: t("why_join.benefit3.desc") },
    { icon: Globe, title: t("why_join.benefit4.title"), description: t("why_join.benefit4.desc") },
  ]

  return (
    <main className="flex min-h-screen flex-col">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-b from-black/5 to-background dark:from-white/5 px-4 lg:px-12">
        <div className="container mx-auto">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Badge variant="outline" className="mb-4 border-black dark:border-white text-black dark:text-white font-bold uppercase tracking-wider">
              {t("hero.badge")}
            </Badge>
            <h1 className="text-4xl md:text-6xl  tracking-tight uppercase">
              {t("hero.title")}
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="default" className="h-12 px-8 rounded-2xl bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 font-bold transition-all" asChild>
                <Link href="/careers/jobs">
                  {t("hero.view_jobs")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="default" variant="outline" className="h-12 px-8 rounded-2xl border-black/20 dark:border-white/20 text-black dark:text-white hover:bg-black dark:hover:bg-white dark:hover:text-black font-bold transition-all" asChild>
                <Link href="#values">
                  {t("hero.our_values")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section id="values" className="py-16 md:py-24 px-4 lg:px-12">
        <div className="container mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-5xl  uppercase tracking-tight">{v("title")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {v("subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="p-8 rounded-[2rem] bg-background border border-black/5 dark:border-white/10 space-y-6 text-center hover:shadow-2xl hover:shadow-black/5 transition-all duration-500">
                <div className="w-16 h-16 rounded-2xl bg-black dark:bg-white flex items-center justify-center mx-auto shadow-lg shadow-black/10">
                  <value.icon className="h-8 w-8 text-white dark:text-black" />
                </div>
                <h3 className="text-2xl  uppercase tracking-tight">{value.title}</h3>
                <p className="text-muted-foreground text-sm text-left leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 px-4 lg:px-12">
        <div className="container mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-5xl  uppercase tracking-tight">{t("why_join.title")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t("why_join.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-start gap-4 p-8 rounded-[2rem] border border-black/5 dark:border-white/10 bg-card hover:bg-muted/50 transition-colors">
                <div className="w-14 h-14 rounded-2xl bg-black dark:bg-white flex items-center justify-center shrink-0 shadow-lg shadow-black/5">
                  <benefit.icon className="h-6 w-6 text-white dark:text-black" />
                </div>
                <div className="space-y-1">
                  <h3 className=" text-xl uppercase tracking-tight">{benefit.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-black px-4 lg:px-12">
        <div className="container mx-auto text-center space-y-6">
          <div className="w-20 h-20 rounded-3xl bg-black dark:bg-white flex items-center justify-center mx-auto shadow-xl shadow-black/10">
            <Briefcase className="h-10 w-10 text-white dark:text-black" />
          </div>
          <h2 className="text-3xl md:text-5xl uppercase tracking-tight text-white">
            {t("cta.title")}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("cta.subtitle")}
          </p>
          <Button size="default" className="h-12 px-8 rounded-2xl bg-white dark:bg-white text-black dark:text-black hover:bg-black/90 dark:hover:bg-white/90 font-bold uppercase tracking-wide transition-all" asChild>
            <Link href="/careers/jobs">
              {t("cta.button")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </main>
  )
}
